<script setup>
/**
 * "How it answered" — the per-message disclosure on the Test it screen. THE PLAN, §4 P6,
 * extended by step-driven tasks (project 1, delivery 1a).
 *
 * The old build printed this as `trace · 2 tools · 3 KB hits · ~1,240 tokens` inside a
 * `<details>`. Every noun in that line was ours, not the owner's, and the one number a
 * shop owner could read — tokens — was the only one that meant nothing to her while
 * quietly being our cost of goods. So the BFF now hands over a trace already spoken in
 * this screen's words (`routes/api/playground.ts`, `testReplyDTO`) and this component
 * only decides what is worth showing.
 *
 * WHAT 1a ADDED, AND THE TWO HOLES IT CLOSED. A turn driven by the step engine used to
 * show almost nothing here: the four blocks below knew that A task had been followed and
 * not which step it had reached, what it had written down, which of the owner's systems it
 * had called, or why a question had come round a second time. And a draft the reply gate
 * WITHHELD arrived as a bare handover line — indistinguishable from the assistant deciding
 * for itself that a person was needed, though the two are opposite problems and have
 * opposite fixes. Both are now sections of their own, and the withheld one is announced in
 * the collapsed summary as well, because the owner who needs it is the owner who has not
 * opened this yet.
 *
 * FOUR RULES IT KEEPS:
 *
 *  • COLLAPSED BY DEFAULT, ALWAYS PRESENT. The answer is what the owner came for; the
 *    reasoning is what she opens when the answer is wrong. A trace that is open by default
 *    turns every turn into a wall of JSON, and one that appears only "when interesting"
 *    teaches her it is not always there.
 *  • THE SUMMARY IS THE HEADLINE, NOT A COUNT OF INTERNALS. `used 2 connections, read 3
 *    saved answers` says what happened. `2 tools · 3 hits` says what our architecture is
 *    called.
 *  • REHEARSED vs REALLY RAN IS PRINTED ON EVERY ROW, in ruby when it was real — on a
 *    task's connections exactly as on a tool call. The screen-level switch is the
 *    permission; this is the receipt. An owner who scrolls back two turns must be able to
 *    see which of them touched her real systems without remembering where the switch was
 *    at the time.
 *  • A CONNECTION'S ANSWER IS NEVER SHOWN. Not truncated, not folded — not sent to this
 *    screen at all, by the BFF. It can carry another customer's details and a sign-in the
 *    owner's own system chose. The row says which system and whether it answered, and the
 *    block says so out loud rather than leaving the absence to be noticed.
 *
 * WHERE THE WIRE SHAPE LIVES: `helpers/test-trace.js`, and nowhere else in this file. The
 * step trace crosses two repos and was named in both at once (the sidecar's `trace.task`
 * is the BFF's `trace.stepTask`, because `task` on that wire is already the free-text
 * playbook pointer), so every field name is read once, in one adapter, with one spec. A
 * template that reached for `run.connections[i].ok` in one `v-if` and `run.next` in
 * another would turn a rename into a dozen silent empty sections.
 *
 * `<bdi>` around every value someone else wrote — a searched phrase, a saved question, a
 * task name, an answer the customer gave — for the reason `helpers/bidi.js` documents at
 * length: `dir="auto"` picks a base direction for the whole sentence, and without an
 * isolate the Arabic run inside an English frame swaps its punctuation onto the wrong side.
 */
import { computed, ref } from 'vue';

import { useWasloI18n } from '../i18n.js';
import { toIntlLocale } from '../helpers/intl.js';
import { MARKER, isolateParts } from '../helpers/bidi.js';
import { normaliseTestTrace } from '../helpers/test-trace.js';

const props = defineProps({
  /** `reply.trace` as the BFF shapes it. Read through `normaliseTestTrace`, never directly. */
  trace: { type: Object, required: true },
  /** `reply.menu` (`{ title, options }`), or null. Drawn as chips by TestThread; named here. */
  menu: { type: Object, default: null },
});

/** P-2.11: "Fetch a sample now" on a live list that had no sample, with the connection's id. */
const emit = defineEmits(['fetch-sample']);

const { t, locale } = useWasloI18n();

const isOpen = ref(false);

/** The ONE read of the wire shape. Everything below is a view of this. */
const view = computed(() => normaliseTestTrace(props.trace));

const connectionActions = computed(() => view.value.connectionActions);
const chatActions = computed(() => view.value.chatActions);
const lookups = computed(() => view.value.lookups);
const task = computed(() => view.value.scenario);
const step = computed(() => view.value.step);
const gate = computed(() => view.value.gate);

const hasTask = computed(() => view.value.hasScenario);
const hasMenu = computed(() => Boolean(props.menu?.title));

/**
 * What the turn actually did, in the order it matters to someone asking "why did it say
 * that": what it held back, what it went and got, what it already knew, what it was in the
 * middle of. Empty means it answered from the Personality page and nothing else — which is
 * a real answer to the question, not an absence, so it is said out loud.
 *
 * THE WITHHELD CHIP COMES FIRST and is the only one that describes a NON-event. A reply
 * that was written and never sent is the single most surprising thing this component can
 * report, and it is reported at the moment the owner is looking at a handover she did not
 * expect — i.e. before she has opened anything.
 *
 * A step-driven turn shows its own chip INSTEAD OF the playbook one, never both: they would
 * read as two tasks, and on such a turn the playbook pointer is empty anyway.
 */
const summary = computed(() => {
  const parts = [];
  if (gate.value?.withheld) parts.push(t('WASLO.TEST.TRACE.CHIP_WITHHELD'));
  // P-5.7: HER connections only. A knowledge search is not "a connection".
  if (connectionActions.value.length > 0) {
    parts.push(
      t('WASLO.TEST.TRACE.CHIP_ACTIONS', connectionActions.value.length)
    );
  }
  if (view.value.matchCount > 0) {
    parts.push(t('WASLO.TEST.TRACE.CHIP_LOOKUPS', view.value.matchCount));
  }
  if (step.value) parts.push(t('WASLO.TEST.TRACE.CHIP_STEP'));
  else if (hasTask.value) parts.push(t('WASLO.TEST.TRACE.CHIP_TASK'));
  if (hasMenu.value) parts.push(t('WASLO.TEST.TRACE.CHIP_MENU'));
  if (parts.length === 0) return t('WASLO.TEST.TRACE.NOTHING');
  return parts.join(t('WASLO.COMMON.LIST_SEPARATOR'));
});

// vue-i18n hands us Chatwoot's directory name (`pt_BR`), which `Intl` rejects outright.
const intlLocale = computed(() => toIntlLocale(locale.value));

const percent = score =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.min(1, Number(score) || 0)));

/**
 * Whatever a connected system was sent or sent back, as text.
 *
 * Bounded, and that bound is not cosmetic: this is the one place on the screen where a
 * value the owner's own server chose is rendered verbatim, and a system that answers with
 * a 200 KB catalogue would otherwise push every later turn off the page. The cut is
 * announced rather than silent — a truncation nobody mentions reads as corrupt data.
 */
const MAX_JSON = 1200;

/**
 * Held here rather than written inline, so the `<pre>` and its content fit on one line in
 * the template. That is not cosmetic: inside a `<pre>` every character between the tags is
 * rendered, so a formatter that wraps the closing tag onto its own line puts its
 * indentation into the payload the owner reads.
 */
const JSON_CLASS =
  'p-2 overflow-auto text-xs rounded-lg max-h-40 bg-n-alpha-2 text-n-slate-11';

const asText = value => {
  if (value === undefined) return '—';
  let text;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    // A cycle or a BigInt. Neither is worth failing a render over.
    text = String(value);
  }
  if (typeof text !== 'string') return String(value);
  return text.length > MAX_JSON
    ? `${text.slice(0, MAX_JSON)}\n${t('WASLO.TEST.TRACE.TRUNCATED')}`
    : text;
};

const queryParts = query =>
  isolateParts(t('WASLO.TEST.TRACE.LOOKUP_QUERY', { query: MARKER }), query);

const startedParts = computed(() =>
  isolateParts(
    t('WASLO.TEST.TRACE.TASK_STARTED', { name: MARKER }),
    task.value.started ?? ''
  )
);

const activeParts = computed(() =>
  isolateParts(
    t('WASLO.TEST.TRACE.TASK_ACTIVE', { name: MARKER }),
    task.value.active ?? ''
  )
);

const menuParts = computed(() =>
  isolateParts(
    t('WASLO.TEST.TRACE.MENU_SHOWN', { title: MARKER }),
    props.menu?.title ?? ''
  )
);

// ── the step-driven task ─────────────────────────────────────────────────────────────

/** P-10.9: one sentence per way a task can start. */
const STARTED_BY_KEY = {
  assistant: 'WASLO.TEST.TRACE.STARTED_BY.ASSISTANT',
  words: 'WASLO.TEST.TRACE.STARTED_BY.WORDS',
  menu: 'WASLO.TEST.TRACE.STARTED_BY.MENU',
  switch: 'WASLO.TEST.TRACE.STARTED_BY.SWITCH',
  rehearsal: 'WASLO.TEST.TRACE.STARTED_BY.REHEARSAL',
};
const startedByText = computed(() => {
  const key = STARTED_BY_KEY[step.value?.startedBy];
  return key ? t(key) : '';
});

/**
 * P-2.11: the line under a connection that did not ask the real system. A sample is dated so
 * the owner can tell a stale one from today's; `simulated` and `live` need no line (the badge
 * beside the name already says which).
 */
const sampleDate = iso => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'medium',
  }).format(at);
};

const sourceLine = connection => {
  if (connection.source === 'sample') {
    const date = connection.sampleAt ? sampleDate(connection.sampleAt) : null;
    return date
      ? t('WASLO.TEST.TRACE.SOURCE_SAMPLE', { date })
      : t('WASLO.TEST.TRACE.SOURCE_SAMPLE_UNDATED');
  }
  if (connection.source === 'no_sample') {
    return t('WASLO.TEST.TRACE.SOURCE_NO_SAMPLE', { name: connection.name });
  }
  return '';
};

/**
 * P-5.7: one call as a sentence, with her own words for the thing (a menu, a connection,
 * a detail) in a bidi isolate, the same way every other frame on this screen holds them.
 */
const TRACE_KIND = 'WASLO.TEST.TRACE.KIND';
const CHAT_COPY = {
  close: 'CLOSE',
  assign: 'ASSIGN',
  add_labels: 'ADD_LABELS',
  remove_labels: 'REMOVE_LABELS',
  note: 'NOTE',
  save_contact: 'SAVE_CONTACT',
};
const KIND_COPY = { lookup: 'LOOKUP', handover: 'HANDOVER' };
const whole = text => ({ before: text, value: '', after: '' });

const actionParts = action => {
  if (action.kind === 'connection') {
    return isolateParts(
      t(`${TRACE_KIND}.CONNECTION`, { label: MARKER }),
      action.label ?? action.name
    );
  }
  if (action.kind === 'menu') {
    return action.label
      ? isolateParts(t(`${TRACE_KIND}.MENU`, { label: MARKER }), action.label)
      : whole(t(`${TRACE_KIND}.MENU_UNNAMED`));
  }
  if (action.kind === 'chat_action') {
    const copy = CHAT_COPY[action.action] ?? 'OTHER';
    if (copy === 'SAVE_CONTACT') {
      return action.label
        ? isolateParts(
            t(`${TRACE_KIND}.SAVE_CONTACT`, { label: MARKER }),
            action.label
          )
        : whole(t(`${TRACE_KIND}.SAVE_CONTACT_UNNAMED`));
    }
    return whole(t(`${TRACE_KIND}.${copy}`));
  }
  return whole(t(`${TRACE_KIND}.${KIND_COPY[action.kind] ?? 'OTHER'}`));
};

const stepTaskParts = computed(() =>
  isolateParts(
    t('WASLO.TEST.TRACE.STEP_TASK', { name: MARKER }),
    step.value?.task ?? ''
  )
);

/**
 * Two wordings per line, chosen by whether the owner's own label for the step was there.
 *
 * "It is on the step “Which day”" is a sentence about her task. "It is on the step marked
 * “day”" is a sentence about an identifier, and the word MARKED is what makes the second
 * honest — an id presented as a name sends her hunting through the builder for a step
 * called `day`, which is not what anything is called there.
 */
const stepOnParts = computed(() =>
  isolateParts(
    t(
      step.value?.stepNamed
        ? 'WASLO.TEST.TRACE.STEP_ON'
        : 'WASLO.TEST.TRACE.STEP_ON_ID',
      { step: MARKER }
    ),
    step.value?.stepName ?? ''
  )
);

const ruleFailedParts = computed(() =>
  isolateParts(
    t(
      step.value?.ruleNamed
        ? 'WASLO.TEST.TRACE.STEP_RULE_FAILED'
        : 'WASLO.TEST.TRACE.STEP_RULE_FAILED_ID',
      { step: MARKER }
    ),
    step.value?.ruleName ?? ''
  )
);

/**
 * The id beside the label. Muted and monospace, because it is not prose: it is the handle
 * that identifies the step in the builder when the graph itself is what went wrong.
 */
const CODE_CLASS =
  'px-1.5 py-0.5 text-xs font-mono rounded bg-n-alpha-2 text-n-slate-10';

const storedParts = answer =>
  isolateParts(
    t('WASLO.TEST.TRACE.STEP_ANSWER_STORED', { value: MARKER }),
    answer.value ?? ''
  );

/**
 * One sentence per outcome the engine can report, keyed by the wire's own word.
 *
 * Module-shaped rather than a chain of `v-else-if`, and unknown-tolerant on purpose: `next`
 * is a plain string on the wire so a value added in the sidecar reaches the screen instead
 * of being refused by a client that shipped first. A word this table has no sentence for
 * draws no line, which is honest — the rest of the block is still true.
 */
const STEP_OUTCOME_COPY = {
  ask: 'WASLO.TEST.TRACE.STEP_NEXT_ASK',
  say: 'WASLO.TEST.TRACE.STEP_NEXT_SAY',
  silent: 'WASLO.TEST.TRACE.STEP_NEXT_SILENT',
  done: 'WASLO.TEST.TRACE.STEP_NEXT_DONE',
  handoff: 'WASLO.TEST.TRACE.STEP_NEXT_HANDOFF',
  none: 'WASLO.TEST.TRACE.STEP_NEXT_NONE',
};

/** P-10.21: a handover caused by the owner's own system saying no, named as that. */
const HANDOVER_CAUSE_COPY = {
  system_refused: 'WASLO.TEST.TRACE.STEP_NEXT_SYSTEM_REFUSED',
};

const outcomeLine = computed(() => {
  const key =
    HANDOVER_CAUSE_COPY[step.value?.handoverCause] ??
    STEP_OUTCOME_COPY[step.value?.outcome];
  return key ? t(key) : null;
});

// ── the reply gate's verdict ─────────────────────────────────────────────────────────

/**
 * Why the draft was stopped, in the owner's terms.
 *
 * `price` and `url` are the two that get a reply WITHHELD: the assistant wrote a figure or
 * a link that nothing it was given backs up. Each has a second wording for the case where
 * the offending text did not come through — the sentence still has to say which rule
 * caught it, because that is the part she can act on.
 *
 * `length` and `language` are soft: the draft was rewritten and went out.
 */
const GATE_REASON_COPY = {
  price: {
    withValue: 'WASLO.TEST.TRACE.GATE_PRICE',
    plain: 'WASLO.TEST.TRACE.GATE_PRICE_PLAIN',
  },
  url: {
    withValue: 'WASLO.TEST.TRACE.GATE_URL',
    plain: 'WASLO.TEST.TRACE.GATE_URL_PLAIN',
  },
  length: { plain: 'WASLO.TEST.TRACE.GATE_LENGTH' },
  language: { plain: 'WASLO.TEST.TRACE.GATE_LANGUAGE' },
  claim: { plain: 'WASLO.TEST.TRACE.GATE_CLAIM' },
  prompt_echo: { plain: 'WASLO.TEST.TRACE.GATE_PROMPT_ECHO' },
  time: {
    withValue: 'WASLO.TEST.TRACE.GATE_TIME',
    plain: 'WASLO.TEST.TRACE.GATE_TIME_PLAIN',
  },
  list: {
    withValue: 'WASLO.TEST.TRACE.GATE_LIST',
    plain: 'WASLO.TEST.TRACE.GATE_LIST_PLAIN',
  },
};

/** A kind this screen shipped before. It still renders — an unnamed rule beats silence. */
const GATE_REASON_FALLBACK = 'WASLO.TEST.TRACE.GATE_OTHER';

const gateReasonParts = reason => {
  const copy = GATE_REASON_COPY[reason.kind] ?? {};
  if (copy.withValue && reason.value !== null) {
    return isolateParts(t(copy.withValue, { value: MARKER }), reason.value);
  }
  // No MARKER in these, so `isolateParts` hands back the whole sentence as `before`.
  return isolateParts(t(copy.plain ?? GATE_REASON_FALLBACK), '');
};
</script>

<template>
  <div class="flex flex-col" data-waslo-trace>
    <button
      type="button"
      class="flex items-center min-w-0 gap-2 py-1 text-sm text-n-slate-11 hover:text-n-slate-12"
      :aria-expanded="isOpen"
      data-waslo-trace-toggle
      @click="isOpen = !isOpen"
    >
      <!-- Rotates through 180°, never 90°: a chevron that turns sideways points the wrong
           way the moment the account is Arabic, and there is no logical `rotate`. -->
      <span
        class="transition-transform duration-200 shrink-0 size-4 i-lucide-chevron-down"
        :class="isOpen ? 'rotate-180' : ''"
        aria-hidden="true"
      />
      <span class="font-medium">
        {{ isOpen ? t('WASLO.TEST.TRACE.HIDE') : t('WASLO.TEST.TRACE.SHOW') }}
      </span>
      <!-- Amber, matching the handover line above it: a withheld reply is why that line is
           there, and the two must read as one event rather than two. -->
      <span
        v-if="gate?.withheld"
        class="shrink-0 size-4 text-n-amber-11 i-lucide-shield-alert"
        aria-hidden="true"
        data-waslo-trace-withheld
      />
      <span v-if="!isOpen" class="min-w-0 truncate text-n-slate-10">
        {{ summary }}
      </span>
    </button>

    <div v-if="isOpen" class="flex flex-col gap-5 pt-3" data-waslo-trace-body>
      <!-- 1 · WHAT IT WROTE AND DID NOT SEND. First of everything, because it is the only
           block that explains an ABSENT reply — and the owner arrived here from a handover
           line she did not expect. -->
      <section
        v-if="gate"
        class="flex flex-col gap-2 p-3 outline outline-1 rounded-xl outline-n-amber-8 bg-n-solid-1"
        data-waslo-trace-gate
      >
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.GATE_HEADING') }}
        </h4>
        <p
          v-if="gate.withheld"
          class="mb-0 text-sm leading-6 text-n-amber-11"
          dir="auto"
        >
          {{ t('WASLO.TEST.TRACE.GATE_WITHHELD') }}
        </p>
        <p
          v-else-if="gate.retried"
          class="mb-0 text-sm leading-6 text-n-slate-11"
          dir="auto"
        >
          {{ t('WASLO.TEST.TRACE.GATE_RETRIED') }}
        </p>
        <ul v-if="gate.reasons.length" class="flex flex-col gap-1 mb-0">
          <li
            v-for="(reason, index) in gate.reasons"
            :key="`${reason.kind}-${index}`"
            class="mb-0 text-sm break-words text-n-slate-11"
            dir="auto"
            data-waslo-trace-gate-reason
          >
            <span>{{ gateReasonParts(reason).before }}</span>
            <bdi class="text-n-slate-12">{{
              gateReasonParts(reason).value
            }}</bdi>
            <span>{{ gateReasonParts(reason).after }}</span>
          </li>
        </ul>
      </section>

      <!-- 2 · WHERE THE TASK HAD GOT TO. Before the systems and the saved answers, because
           on a task-driven turn it is the frame those two sit inside. -->
      <section v-if="step" class="flex flex-col gap-3" data-waslo-trace-step>
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.STEP_HEADING') }}
        </h4>

        <div class="flex flex-col gap-1">
          <p
            v-if="step.task"
            class="mb-0 text-sm text-n-slate-11"
            dir="auto"
            data-waslo-trace-step-task
          >
            <span>{{ stepTaskParts.before }}</span>
            <bdi class="text-n-slate-12">{{ stepTaskParts.value }}</bdi>
            <span>{{ stepTaskParts.after }}</span>
          </p>
          <p
            v-if="startedByText"
            class="mb-0 text-sm text-n-slate-11"
            data-waslo-trace-started-by
          >
            {{ startedByText }}
          </p>
          <p
            v-if="step.stepName"
            class="mb-0 text-sm text-n-slate-11"
            dir="auto"
            data-waslo-trace-step-on
          >
            <span>{{ stepOnParts.before }}</span>
            <bdi class="text-n-slate-12">{{ stepOnParts.value }}</bdi>
            <span>{{ stepOnParts.after }}</span>
            <!-- Kept beside her own wording, never instead of it. -->
            <bdi
              v-if="step.stepCode"
              :class="CODE_CLASS"
              data-waslo-trace-step-code
            >
              {{ step.stepCode }}
            </bdi>
          </p>
          <!-- Amber: this is the one line in the block that names something that went
               wrong, and it is the answer to "why did it ask that twice?". -->
          <p
            v-if="step.ruleName"
            class="mb-0 text-sm leading-6 text-n-amber-11"
            dir="auto"
            data-waslo-trace-step-rule
          >
            <span>{{ ruleFailedParts.before }}</span>
            <bdi>{{ ruleFailedParts.value }}</bdi>
            <span>{{ ruleFailedParts.after }}</span>
            <bdi v-if="step.ruleCode" :class="CODE_CLASS">
              {{ step.ruleCode }}
            </bdi>
          </p>
          <p
            v-if="outcomeLine"
            class="mb-0 text-sm text-n-slate-11"
            dir="auto"
            data-waslo-trace-step-next
          >
            {{ outcomeLine }}
          </p>
          <p
            v-if="step.sentAsWritten"
            class="mb-0 text-sm text-n-slate-11"
            dir="auto"
            data-waslo-trace-step-as-written
          >
            {{ t('WASLO.TEST.TRACE.STEP_SENT_AS_WRITTEN') }}
          </p>
        </div>

        <!-- WHAT IT HOLDS. Capped in height rather than in rows: the BFF sends at most
             fifty, and fifty rows of a disclosure would push the next two turns off the
             page for someone scrolling back through a chat. -->
        <div class="flex flex-col gap-2">
          <span class="text-xs text-n-slate-10">
            {{ t('WASLO.TEST.TRACE.STEP_ANSWERS') }}
          </span>
          <p
            v-if="!step.answers.length"
            class="mb-0 text-sm text-n-slate-10"
            dir="auto"
          >
            {{ t('WASLO.TEST.TRACE.STEP_ANSWERS_NONE') }}
          </p>
          <ul
            v-else
            class="flex flex-col gap-2 mb-0 overflow-auto max-h-64"
            data-waslo-trace-step-answers
          >
            <li
              v-for="(answer, index) in step.answers"
              :key="`${answer.key}-${index}`"
              class="flex flex-col gap-0.5 p-2 rounded-lg bg-n-alpha-2"
              data-waslo-trace-step-answer
            >
              <div class="flex flex-wrap items-baseline min-w-0 gap-2">
                <bdi v-if="answer.key" class="text-xs text-n-slate-10">
                  {{ answer.key }}
                </bdi>
                <bdi class="min-w-0 text-sm break-words text-n-slate-12">
                  {{ answer.label }}
                </bdi>
              </div>
              <!-- Only when the two differ. A tap on "Tomorrow, 4pm" travels on to a
                   connected system as something else entirely, and a mapping that has gone
                   wrong is invisible if only one of the two is printed. -->
              <p
                v-if="answer.value"
                class="mb-0 text-xs break-words text-n-slate-10"
                dir="auto"
                data-waslo-trace-step-answer-stored
              >
                <span>{{ storedParts(answer).before }}</span>
                <bdi>{{ storedParts(answer).value }}</bdi>
                <span>{{ storedParts(answer).after }}</span>
              </p>
            </li>
          </ul>
        </div>

        <!-- WHAT IT DID NOT KEEP (P-10.3) and WHAT IT DID NOT SEND AGAIN (P-10.22). One line per
             step: the owner's name for it, then why. -->
        <ul
          v-if="step.dropped.length || step.alreadyDone.length"
          class="flex flex-col gap-1 mb-0"
          data-waslo-trace-step-notes
        >
          <li
            v-for="(name, index) in step.dropped"
            :key="`dropped-${index}`"
            class="flex flex-wrap items-baseline min-w-0 gap-2 text-sm text-n-slate-11"
            data-waslo-trace-step-dropped
          >
            <bdi class="text-n-slate-12">{{ name }}</bdi>
            <span>{{ t('WASLO.TEST.TRACE.STEP_DROPPED') }}</span>
          </li>
          <li
            v-for="(name, index) in step.alreadyDone"
            :key="`done-${index}`"
            class="flex flex-wrap items-baseline min-w-0 gap-2 text-sm text-n-slate-11"
            data-waslo-trace-step-not-sent
          >
            <bdi class="text-n-slate-12">{{ name }}</bdi>
            <span>{{ t('WASLO.TEST.TRACE.STEP_NOT_SENT_AGAIN') }}</span>
          </li>
        </ul>

        <!-- WHAT IT CHECKED. Name and verdict only — the answer itself is never sent to
             this screen, and the line under the list says so rather than leaving an owner
             to wonder what is being hidden from her. -->
        <div v-if="step.connections.length" class="flex flex-col gap-2">
          <span class="text-xs text-n-slate-10">
            {{ t('WASLO.TEST.TRACE.STEP_CONNECTIONS') }}
          </span>
          <ul class="flex flex-col gap-2 mb-0">
            <li
              v-for="(connection, index) in step.connections"
              :key="`${connection.name}-${index}`"
              class="flex flex-wrap items-center min-w-0 gap-2"
              data-waslo-trace-connection
            >
              <bdi class="min-w-0 text-sm break-words text-n-slate-12">
                {{ connection.name }}
              </bdi>
              <span
                v-if="connection.worked !== null"
                class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
                :class="
                  connection.worked
                    ? 'bg-n-alpha-2 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
                data-waslo-trace-connection-result
              >
                {{
                  connection.worked
                    ? t('WASLO.TEST.TRACE.STEP_CONNECTION_OK')
                    : t('WASLO.TEST.TRACE.STEP_CONNECTION_FAILED')
                }}
              </span>
              <!-- The same receipt the tool-call rows carry, in the same colours: "it
                   worked" about a system nobody called is the one lie this screen must
                   not tell. -->
              <span
                v-if="connection.rehearsed !== null"
                class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
                :class="
                  connection.rehearsed
                    ? 'bg-n-alpha-2 text-n-slate-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'
                "
                data-waslo-trace-connection-mode
              >
                {{
                  connection.rehearsed
                    ? t('WASLO.TEST.TRACE.ACTION_REHEARSED')
                    : t('WASLO.TEST.TRACE.ACTION_LIVE')
                }}
              </span>
              <!-- P-2.11: where the answer came from, when it was not the real system. -->
              <span
                v-if="sourceLine(connection)"
                class="basis-full text-xs text-n-slate-11"
                :data-waslo-trace-connection-source="connection.source"
              >
                {{ sourceLine(connection) }}
              </span>
              <button
                v-if="
                  connection.source === 'no_sample' &&
                  connection.connectionId !== null
                "
                type="button"
                class="text-xs underline text-n-blue-11"
                data-waslo-trace-fetch-sample
                @click="emit('fetch-sample', connection.connectionId)"
              >
                {{ t('WASLO.TEST.TRACE.FETCH_SAMPLE') }}
              </button>
            </li>
          </ul>
          <p class="mb-0 text-xs text-n-slate-10" dir="auto">
            {{ t('WASLO.TEST.TRACE.STEP_CONNECTIONS_NOTE') }}
          </p>
        </div>
      </section>

      <!-- 3 · WHAT IT ASKED HER SYSTEMS. Only a connection leaves the chat (P-5.7), so
           only a connection says Rehearsed or Really ran. -->
      <section v-if="connectionActions.length" class="flex flex-col gap-2">
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.ACTIONS_HEADING') }}
        </h4>
        <div
          v-for="(action, index) in connectionActions"
          :key="`${action.name}-${index}`"
          class="flex flex-col gap-3 p-3 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
          data-waslo-trace-action
        >
          <div class="flex flex-wrap items-center gap-2">
            <p class="mb-0 text-sm font-medium text-n-slate-12" dir="auto">
              <span>{{ actionParts(action).before }}</span>
              <bdi>{{ actionParts(action).value }}</bdi>
              <span>{{ actionParts(action).after }}</span>
            </p>
            <span
              class="px-2 py-0.5 text-xs font-medium rounded-full"
              :class="
                action.rehearsed
                  ? 'bg-n-alpha-2 text-n-slate-11'
                  : 'bg-n-ruby-3 text-n-ruby-11'
              "
              data-waslo-trace-action-mode
            >
              {{
                action.rehearsed
                  ? t('WASLO.TEST.TRACE.ACTION_REHEARSED')
                  : t('WASLO.TEST.TRACE.ACTION_LIVE')
              }}
            </span>
          </div>
          <div
            v-if="action.fields.length"
            class="flex flex-col gap-1"
            data-waslo-trace-action-sent
          >
            <span class="text-xs text-n-slate-10">
              {{ t('WASLO.TEST.TRACE.ACTION_SENT') }}
            </span>
            <p
              v-for="(row, at) in action.fields"
              :key="`sent-${at}`"
              class="flex gap-2 mb-0 text-sm"
            >
              <bdi class="shrink-0 text-n-slate-10">{{ row.label }}</bdi>
              <bdi class="min-w-0 break-words text-n-slate-12">{{
                row.value
              }}</bdi>
            </p>
          </div>
          <div
            v-if="action.received.length"
            class="flex flex-col gap-1"
            data-waslo-trace-action-got
          >
            <span class="text-xs text-n-slate-10">
              {{ t('WASLO.TEST.TRACE.ACTION_GOT') }}
            </span>
            <p
              v-for="(row, at) in action.received"
              :key="`got-${at}`"
              class="flex gap-2 mb-0 text-sm"
            >
              <bdi class="shrink-0 text-n-slate-10">{{ row.label }}</bdi>
              <bdi class="min-w-0 break-words text-n-slate-12">{{
                row.value
              }}</bdi>
            </p>
          </div>
          <!-- The raw data, folded: for the developer who wired the connection, not for
               the owner reading what happened. -->
          <details class="text-xs" data-waslo-trace-action-technical>
            <summary class="cursor-pointer text-n-slate-11">
              {{ t('WASLO.TEST.TRACE.TECHNICAL') }}
            </summary>
            <div class="grid gap-3 pt-2 md:grid-cols-2">
              <div class="flex flex-col min-w-0 gap-1">
                <span class="text-xs text-n-slate-10">
                  {{ t('WASLO.TEST.TRACE.ACTION_SENT') }}
                </span>
                <!-- prettier-ignore -->
                <pre dir="ltr" :class="JSON_CLASS">{{ asText(action.input) }}</pre>
              </div>
              <div class="flex flex-col min-w-0 gap-1">
                <span class="text-xs text-n-slate-10">
                  {{ t('WASLO.TEST.TRACE.ACTION_GOT') }}
                </span>
                <!-- prettier-ignore -->
                <pre dir="ltr" :class="JSON_CLASS">{{ asText(action.output) }}</pre>
              </div>
            </div>
          </details>
        </div>
      </section>

      <!-- 3b · WHAT IT DID INSIDE THIS CHAT. Nothing outside was touched: no badge. -->
      <section
        v-if="chatActions.length"
        class="flex flex-col gap-2"
        data-waslo-trace-inside
      >
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.INSIDE_HEADING') }}
        </h4>
        <ul class="flex flex-col gap-1 mb-0 list-none">
          <li
            v-for="(action, index) in chatActions"
            :key="`${action.name}-${index}`"
            class="text-sm text-n-slate-11"
            dir="auto"
            :data-waslo-trace-inside-kind="action.kind"
          >
            <span>{{ actionParts(action).before }}</span>
            <bdi>{{ actionParts(action).value }}</bdi>
            <span>{{ actionParts(action).after }}</span>
          </li>
        </ul>
      </section>

      <!-- 4 · WHAT IT ALREADY KNEW. The gap between what the customer wrote and what it
           searched for is often the whole explanation for a wrong answer. -->
      <section v-if="lookups.length" class="flex flex-col gap-2">
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.LOOKUPS_HEADING') }}
        </h4>
        <div
          v-for="(lookup, index) in lookups"
          :key="`${lookup.query}-${index}`"
          class="flex flex-col gap-2 p-3 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
          data-waslo-trace-lookup
        >
          <!-- Three nodes, never one string: the middle one is a bidi ISOLATE. The spaces
               that belong to the sentence are INSIDE the interpolated parts, and Vue's
               `condense` drops the newline-bearing whitespace between these tags. Same
               construction as `AttentionRow`, for the same reason. -->
          <p class="mb-0 text-sm text-n-slate-11" dir="auto">
            <span>{{ queryParts(lookup.query).before }}</span>
            <bdi class="text-n-slate-12">{{
              queryParts(lookup.query).value
            }}</bdi>
            <span>{{ queryParts(lookup.query).after }}</span>
          </p>
          <p
            v-if="!lookup.matches.length"
            class="mb-0 text-sm text-n-slate-10"
            dir="auto"
          >
            {{ t('WASLO.TEST.TRACE.LOOKUP_NONE') }}
          </p>
          <ul v-else class="flex flex-col gap-2 mb-0">
            <li
              v-for="match in lookup.matches"
              :key="match.question"
              class="flex items-start justify-between min-w-0 gap-3"
            >
              <bdi class="min-w-0 text-sm text-n-slate-12">
                {{ match.question }}
              </bdi>
              <span
                class="px-2 py-0.5 text-xs rounded-full shrink-0 tabular-nums bg-n-alpha-2 text-n-slate-11"
              >
                {{ percent(match.score) }}
              </span>
            </li>
          </ul>
        </div>
      </section>

      <!-- 5 · WHAT IT WAS IN THE MIDDLE OF. The free-text playbook, which predates the
           step engine and reports only that it was running. -->
      <section v-if="hasTask" class="flex flex-col gap-2">
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.TASK_HEADING') }}
        </h4>
        <p
          v-if="task.started"
          class="mb-0 text-sm text-n-slate-11"
          dir="auto"
          data-waslo-trace-task-started
        >
          <span>{{ startedParts.before }}</span>
          <bdi class="text-n-slate-12">{{ startedParts.value }}</bdi>
          <span>{{ startedParts.after }}</span>
        </p>
        <p v-if="task.completed" class="mb-0 text-sm text-n-slate-11">
          {{ t('WASLO.TEST.TRACE.TASK_COMPLETED') }}
        </p>
        <p v-if="task.active" class="mb-0 text-sm text-n-slate-11" dir="auto">
          <span>{{ activeParts.before }}</span>
          <bdi class="text-n-slate-12">{{ activeParts.value }}</bdi>
          <span>{{ activeParts.after }}</span>
        </p>
      </section>

      <!-- 6 · WHAT IT OFFERED TO TAP. -->
      <section v-if="hasMenu" class="flex flex-col gap-2" data-waslo-trace-menu>
        <h4 class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WASLO.TEST.TRACE.MENU_HEADING') }}
        </h4>
        <p class="mb-0 text-sm text-n-slate-11" dir="auto">
          <span>{{ menuParts.before }}</span>
          <bdi class="text-n-slate-12">{{ menuParts.value }}</bdi>
          <span>{{ menuParts.after }}</span>
        </p>
      </section>

      <!-- Nothing at all is a real answer to "why did it say that", so it is said, not
           left as an empty disclosure the owner opened for nothing. `isEmpty` covers the
           trace; the menu is a separate prop and is added here. -->
      <p
        v-if="view.isEmpty && !hasMenu"
        class="mb-0 text-sm text-n-slate-11"
        data-waslo-trace-empty
      >
        {{ t('WASLO.TEST.TRACE.NOTHING_BODY') }}
      </p>
    </div>
  </div>
</template>
